import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  modoPerigoso,
  ROTAS,
  TETO_LISTA_SESSOES,
  tituloDaSessao,
  type EstadoSessao,
  type IdModoPermissao,
  type UsoDaSessao,
} from '@remoto/shared';
import { chamar } from '../api';
import { mensagemDoErro } from '../erros';
import { porcento, tempoRelativo } from '../formato';
import { usarUsoDasSessoes } from '../uso/consultas';
import { descreverAssinaturaDaSessao, descreverUsoDaSessao } from '../uso/cota';
import { SeloAssinatura } from '../componentes/SeloAssinatura';
import {
  IconeConfirmado,
  IconeFechar,
  IconeMais,
  IconeAbrir,
  IconeFechado,
  IconeMaisOpcoes,
  IconeMestre,
  IconeMostrar,
  IconeRecomecar,
  IconeSemPortao,
  IconeSilenciada,
} from '../componentes/icones';
import { socketDoPainel } from '../socket/painel';
import { usarSessao } from '../estado/sessao';
import { CriarMestre } from './CriarMestre';
import { usarConfirmacao } from '../componentes/Modal';
import { BarraDeSessoes } from './BarraDeSessoes';
import {
  lerArranjoDeSessoes,
  gravarArranjoDeSessoes,
  lerMestresExpandidas,
  gravarMestresExpandidas,
} from './memoria';
import { acharMestres, ehMestreVigente } from '../sessoes/mestre';
import { MOTORES, ROTULO_DO_MOTOR, type Motor } from '@remoto/protocolo';
import { SeloDoMotorNaLista } from '../componentes/SeloDoMotor';
import {
  agruparSessoes,
  chaveDoProjeto,
  nomeDoProjeto,
  ordenarSessoes,
  pastaCurta,
  projetosDe,
  filtrarFilhasDaMestre,
  separarDaMestre,
  type ArranjoDeSessoes,
  type FilhasDaMestre,
} from './arranjoDeSessoes';

export { pastaCurta };

export interface SessaoNaLista {
  id: string;
  maquinaId: string;
  maquinaNome: string;
  maquinaPapel: string;
  cwd: string;
  nome: string | null;
  /** Como a máquina chama a conversa; nunca vence o que a pessoa escolheu. */
  nomeMaquina?: string | null;
  tituloIa: string | null;
  estado: EstadoSessao;
  origem: string;
  /** NORMAL ou MESTRE (F13): a mestre sai dos grupos e fica fixada no topo. */
  funcao?: string;
  /** CLAUDE, CODEX ou OPENCODE. Ausente = CLAUDE, como no resto da casa. */
  motor?: string;
  /** Fora da lista, mas viva. Nulo quer dizer que ela aparece. */
  arquivadaEm?: string | null;
  ultimoEventoEm: string | null;
  ultimoSeq: number;
  /** Quando a máquina avisou que o último turno terminou. */
  ultimoTurnoFimEm?: string | null;
  /** Até que fim de turno esta pessoa já viu esta sessão. */
  turnoFimVisto?: string | null;
  /** Esta pessoa mandou esta conversa parar de avisar. */
  silenciada?: boolean;
  /**
   * Modo de permissão. A API já mandava (o `listar` não usa `select`); o que
   * faltava era o campo aqui e a pílula na linha — sem eles, uma conversa que
   * roda SEM PORTÃO parecia igual a uma que pergunta a cada passo.
   */
  modo?: string | null;
  /** Ids das mestres vigentes que vigiam esta conversa (a API resolve a herança). */
  vigiadaPor?: string[];
}

/**
 * A pílula de modo só aparece quando há o que confessar: `auto` e
 * `bypassPermissions` agem sem cartão. Modo que pergunta não vira selo —
 * marcar todas as linhas seria ruído, e ruído se aprende a ignorar.
 *
 * Os dois não são o mesmo aviso, e a cor separa: `auto` ainda tem um
 * classificador que nega e escala (âmbar, atenção); `bypassPermissions` não
 * checa nada (vermelho, perigo). É a mesma marcação `perigoso` do
 * `MODOS_PERMISSAO`, que o formulário e o seletor já usam.
 */
function SeloDeModo({ modo }: { modo?: string | null }) {
  if (modo !== 'auto' && modo !== 'bypassPermissions') return null;
  const perigoso = modoPerigoso(modo as IdModoPermissao);
  return (
    <span
      data-testid={'selo-modo-' + modo}
      title={
        perigoso
          ? 'Ignorar permissões: esta conversa age sem pedir aprovação e sem nada negar'
          : 'Automático: age sem cartão, mas um classificador ainda nega e pede você no que é grave'
      }
      className={
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ' +
        (perigoso ? 'bg-red-950/70 text-red-300' : 'bg-amber-950/60 text-amber-300')
      }
    >
      <IconeSemPortao tamanho={10} />
      {perigoso ? 'sem portão' : 'auto'}
    </span>
  );
}

/**
 * O quanto da semana da conta esta conversa gastou — estimativa por rateio.
 *
 * Só de 1% para cima: marcar trinta linhas com "<1%" faria o número virar
 * textura, e o que se procura na lista é quem está pesando. O custo e o
 * contexto vão no `title`, onde cabem sem disputar com o nome.
 */
function SeloDeUso({ uso }: { uso: UsoDaSessao | undefined }) {
  /*
    A sessão do NanoGPT (F2, D45) não tem % da semana — a assinatura é da
    conta —, mas o modelo dela pode estar INCLUÍDO na assinatura: é o selo que
    a lista mostra, com o equivalente e o contexto no `title`.
  */
  const daAssinatura = descreverAssinaturaDaSessao(uso);
  if (daAssinatura) {
    return (
      <span title={daAssinatura} aria-label={daAssinatura} className="mr-1">
        <SeloAssinatura assinatura={uso?.assinatura} />
      </span>
    );
  }
  const descricao = descreverUsoDaSessao(uso);
  if (!descricao || !uso || uso.pctSemanal === null) return null;
  return (
    <span
      data-testid="selo-uso"
      title={descricao}
      aria-label={descricao}
      className="mr-1 rounded bg-zinc-800 px-1 py-0.5 text-[10px] tabular-nums text-zinc-300"
    >
      {porcento(uso.pctSemanal)}
    </span>
  );
}

const COR: Record<EstadoSessao, string> = {
  ATIVA: 'bg-emerald-400',
  AGUARDANDO_PERMISSAO: 'bg-amber-400',
  DORMINDO: 'bg-zinc-600',
  ENCERRADA: 'bg-zinc-700',
};

const ROTULO: Record<EstadoSessao, string> = {
  ATIVA: 'ativa',
  AGUARDANDO_PERMISSAO: 'esperando permissão',
  DORMINDO: 'dormindo',
  ENCERRADA: 'encerrada',
};

/**
 * O nome que a lista mostra.
 *
 * A cadeia mora em `tituloDaSessao`, no pacote compartilhado — a mesma que a
 * API usa no push e no cartão de permissão. Estava copiada em oito lugares, e
 * uma das cópias tinha invertido a ordem.
 */
export const nomeDaSessao = tituloDaSessao;

/**
 * Concluiu e você ainda não viu.
 *
 * Compara carimbo com carimbo, os dois do servidor: comparar com o relógio
 * local marcaria como novidade tudo o que chegasse de um aparelho adiantado.
 * E é `ultimoTurnoFimEm`, não `ultimoSeq`, porque o pedido é "terminou o
 * trabalho" — por `seq` qualquer respiro da sessão acenderia a bolinha.
 */
export function temNovidade(sessao: SessaoNaLista): boolean {
  if (!sessao.ultimoTurnoFimEm) return false;
  if (!sessao.turnoFimVisto) return true;
  /*
    Ordem, e não desigualdade.

    Os dois carimbos chegam por caminhos diferentes — o fim de turno pela
    listagem, o visto pelo evento `sessao.vista` — e por um instante o visto
    pode ser MAIS NOVO do que o fim de turno que esta lista tem em mãos.
    Comparado com `!==`, esse desencontro acendia a bolinha justamente sobre a
    conversa que a pessoa acabara de ler. "Já vi até depois disso" é ter visto.

    Comparar texto funciona porque os dois são ISO-8601 em UTC, carimbados
    pelo servidor (ADR-0011): mesmo formato, mesmo fuso, ordem lexicográfica
    igual à cronológica.
  */
  return sessao.ultimoTurnoFimEm > sessao.turnoFimVisto;
}

/**
 * O quanto esta linha pode chamar a atenção.
 *
 * Silenciar é abaixar o volume, não vendar o dono: a conversa calada que
 * concluiu trabalho continua dando um sinal — só que apagado, do tom das
 * legendas, sem negrito e sem a cor que o olho procura. Sumir com o sinal
 * transformaria "silenciar" em "esconder", e trabalho pronto desapareceria da
 * lista sem ninguém ter pedido isso.
 *
 * `nenhum` para a conversa em dia, `aceso` para a novidade que pode
 * interromper, `discreto` para a novidade de uma conversa silenciada.
 */
export function sinalDeNovidade(sessao: SessaoNaLista): 'nenhum' | 'discreto' | 'aceso' {
  if (!temNovidade(sessao)) return 'nenhum';
  return sessao.silenciada ? 'discreto' : 'aceso';
}

export function ListaDeSessoes({
  sessaoAtiva,
  aoEscolher,
}: {
  sessaoAtiva: string;
  aoEscolher: (sessao: SessaoNaLista) => void;
}): React.JSX.Element {
  const [filtro, setFiltro] = useState('');
  const [mostrarEncerradas, setMostrarEncerradas] = useState(false);
  const [mostrarArquivadas, setMostrarArquivadas] = useState(false);
  const [menuAberto, setMenuAberto] = useState('');
  const [renomeando, setRenomeando] = useState('');
  const [rascunho, setRascunho] = useState('');
  const [criandoMestre, setCriandoMestre] = useState(false);
  // Quais mestres estão com as vigiadas e as anteriores à mostra (por aparelho).
  const [expandidas, setExpandidas] = useState<string[]>(() => lerMestresExpandidas());
  /*
    O arranjo nasce do que ficou guardado no aparelho, e não do padrão.

    Lido na inicialização preguiçosa do `useState` (a função, não o valor): com
    `useState(lerArranjoDeSessoes())` a leitura aconteceria a cada render da
    lista — que roda a cada dez segundos, por causa da sondagem.
  */
  const [arranjo, setArranjo] = useState<ArranjoDeSessoes>(() => lerArranjoDeSessoes());

  function mudarArranjo(mudanca: Partial<ArranjoDeSessoes>): void {
    setArranjo((atual) => {
      const novo = { ...atual, ...mudanca };
      gravarArranjoDeSessoes(novo);
      return novo;
    });
  }
  /*
    A confirmação mora AQUI, e não dentro do menu.

    O menu se fecha ao tocar fora, e o modal é um portal: o primeiro clique
    dentro do diálogo contaria como "fora do menu", desmontaria o menu e
    levaria o diálogo junto, antes de ninguém confirmar nada.
  */
  const [confirmar, dialogo] = usarConfirmacao();
  const campo = useRef<HTMLInputElement>(null);
  const cliente = useQueryClient();

  /*
    As arquivadas ficam fora da consulta, e não só da tela.

    Filtrar no cliente as deixaria disputando as duzentas vagas que o servidor
    devolve — e a primeira coisa que uma lista de arquivadas faz é crescer.
    A chave da consulta carrega a escolha, senão a resposta velha ficaria em
    cache e o botão pareceria não funcionar.
  */
  /*
    A chave carrega a escolha de arquivadas — e por isso ela mora numa
    constante.

    `invalidateQueries({queryKey:['sessoes']})` casa por prefixo e continua
    valendo, mas `setQueryData` casa por chave EXATA: a atualização otimista do
    renomear escrevia em `['sessoes']`, que deixou de ser a chave de ninguém, e
    o nome novo só aparecia depois da volta do servidor. Foi o ensaio de
    renomear que acusou.
  */
  const chaveSessoes = ['sessoes', mostrarArquivadas] as const;

  const { data: sessoes = [], isLoading } = useQuery({
    queryKey: chaveSessoes,
    queryFn: () =>
      chamar<SessaoNaLista[]>(
        mostrarArquivadas ? `${ROTAS.sessoes}?incluirArquivadas=true` : ROTAS.sessoes,
      ),
    refetchInterval: 10_000,
  });

  // Só para decidir se o botão de criar o mestre aparece: a rota é de ADMIN.
  // O papel mora no store de sessão (veio do login), não no /auth/eu.
  const papel = usarSessao((e) => e.usuario?.papel);

  /**
   * A bolinha some nos OUTROS aparelhos na hora.
   *
   * Ver no computador e continuar com o aviso aceso no tablet foi o que o
   * usuário pediu para não acontecer — por isso "visto" é do servidor, e chega
   * pela sala do próprio usuário em vez de esperar o giro de dez segundos.
   */
  useEffect(() => {
    const socket = socketDoPainel();
    const aoVer = (dados: { sessaoId?: string; turnoFimVistoEm?: string | null }): void => {
      if (!dados.sessaoId) return;
      cliente.setQueryData<SessaoNaLista[]>(chaveSessoes, (atuais) =>
        (atuais ?? []).map((s) =>
          s.id === dados.sessaoId ? { ...s, turnoFimVisto: dados.turnoFimVistoEm ?? null } : s,
        ),
      );
    };
    /*
      E o outro lado do mesmo par: o fim de turno.

      Sem isto a lista só descobria o turno novo no giro de dez segundos,
      enquanto o "visto" chegava na hora pelo evento acima. Os dois carimbos
      andando em velocidades diferentes é o que fazia a bolinha piscar sobre
      conversa lida. `sessao.estado` também carrega renomear e arquivar, que
      não trazem carimbo — daí a exigência do campo antes de escrever.
    */
    const aoMudarSessao = (dados: { id?: string; ultimoTurnoFimEm?: string | null }): void => {
      if (!dados.id || !dados.ultimoTurnoFimEm) return;
      cliente.setQueryData<SessaoNaLista[]>(chaveSessoes, (atuais) =>
        (atuais ?? []).map((s) =>
          s.id === dados.id ? { ...s, ultimoTurnoFimEm: dados.ultimoTurnoFimEm } : s,
        ),
      );
    };
    /*
      O silêncio também é da pessoa, não do aparelho.

      Silenciar no computador precisa calar o celular — é o mesmo par de olhos.
      Chega pela sala `usuario:<id>`, como o visto.
    */
    const aoSilenciar = (dados: { sessaoId?: string; silenciada?: boolean }): void => {
      if (!dados.sessaoId) return;
      cliente.setQueryData<SessaoNaLista[]>(chaveSessoes, (atuais) =>
        (atuais ?? []).map((s) =>
          s.id === dados.sessaoId ? { ...s, silenciada: dados.silenciada === true } : s,
        ),
      );
    };
    socket.on('sessao.vista', aoVer);
    socket.on('sessao.estado', aoMudarSessao);
    socket.on('sessao.silenciada', aoSilenciar);
    return () => {
      socket.off('sessao.vista', aoVer);
      socket.off('sessao.estado', aoMudarSessao);
      socket.off('sessao.silenciada', aoSilenciar);
    };
    /*
      `mostrarArquivadas` entra nas dependências porque a chave depende dele.

      `setQueryData` casa por chave EXATA: com o ouvinte preso ao primeiro
      render, alternar "mostrar arquivadas" fazia todo patch cair em
      `['sessoes', false]` — que deixa de ser a chave de ninguém. É a mesma
      armadilha que já tinha mordido a atualização otimista do renomear.
    */
  }, [cliente, mostrarArquivadas]);

  const renomear = useMutation({
    mutationFn: (dados: { id: string; nome: string }) =>
      chamar<unknown>(`${ROTAS.sessoes}/${dados.id}`, {
        method: 'PATCH',
        corpo: { nome: dados.nome },
      }),
    // Otimista: o nome novo aparece antes de o servidor confirmar, e volta ao
    // que era se a chamada falhar.
    onMutate: (dados) => {
      const antes = cliente.getQueryData<SessaoNaLista[]>(chaveSessoes);
      cliente.setQueryData<SessaoNaLista[]>(chaveSessoes, (atuais) =>
        (atuais ?? []).map((s) => (s.id === dados.id ? { ...s, nome: dados.nome } : s)),
      );
      return { antes };
    },
    onError: (_erro, _dados, contexto) => {
      if (contexto?.antes) cliente.setQueryData(chaveSessoes, contexto.antes);
    },
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['sessoes'] }),
  });

  /**
   * Ligar e desligar o aviso desta conversa.
   *
   * Otimista, como o renomear: o sino cortado aparece no clique. O servidor
   * confirma pelo evento `sessao.silenciada`, que também acerta os outros
   * aparelhos — e se a chamada falhar, a linha volta ao que era.
   */
  const silenciar = useMutation({
    mutationFn: (dados: { id: string; silenciada: boolean }) =>
      chamar<unknown>(`${ROTAS.sessoes}/${dados.id}/silenciar`, {
        method: 'POST',
        corpo: { silenciada: dados.silenciada },
      }),
    onMutate: (dados) => {
      const antes = cliente.getQueryData<SessaoNaLista[]>(chaveSessoes);
      cliente.setQueryData<SessaoNaLista[]>(chaveSessoes, (atuais) =>
        (atuais ?? []).map((s) => (s.id === dados.id ? { ...s, silenciada: dados.silenciada } : s)),
      );
      return { antes };
    },
    onError: (_erro, _dados, contexto) => {
      if (contexto?.antes) cliente.setQueryData(chaveSessoes, contexto.antes);
    },
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['sessoes'] }),
  });
  /*
    Toda ação da lista precisa dizer quando falha.

    A primeira versão destas três não tratava erro nenhum: o clique saía, a
    chamada voltava 409, e a tela não dizia nada — a conversa continuava ali e
    parecia que o botão não funcionava. Um botão que falha em silêncio é pior
    que um botão que não existe, porque a pessoa tenta de novo.
  */
  const [erroDaAcao, setErroDaAcao] = useState('');

  const arquivar = useMutation({
    mutationFn: (dados: { id: string; arquivada: boolean }) =>
      chamar<unknown>(`${ROTAS.sessoes}/${dados.id}/arquivar`, {
        method: 'POST',
        corpo: { arquivada: dados.arquivada },
      }),
    onError: (e) => setErroDaAcao(mensagemDoErro(e, 'Não foi possível arquivar.')),
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['sessoes'] }),
  });

  /*
    Adormecer é o gesto do meio, e o único que resolve capacidade sem perder
    nada: o processo sai da memória da máquina (a vaga volta) e a conversa fica
    inteira, a um Retomar de distância. Não pede confirmação — como o arquivar,
    e diferente do encerrar: o que se desfaz num clique não precisa de diálogo.
  */
  const adormecer = useMutation({
    mutationFn: (id: string) =>
      chamar<unknown>(`${ROTAS.sessoes}/${id}/adormecer`, { method: 'POST' }),
    onMutate: () => setErroDaAcao(''),
    onError: (e) => setErroDaAcao(mensagemDoErro(e, 'Não foi possível adormecer a conversa.')),
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['sessoes'] }),
  });

  const encerrar = useMutation({
    mutationFn: (id: string) =>
      chamar<unknown>(`${ROTAS.sessoes}/${id}/fechar`, { method: 'POST' }),
    onMutate: () => setErroDaAcao(''),
    onError: (e) => setErroDaAcao(mensagemDoErro(e, 'A máquina não encerrou a sessão.')),
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['sessoes'] }),
  });

  const recomecarMestre = useMutation({
    // O MOTOR vai junto: sem ele, recomecar a mestre do Codex encerraria a do
    // Claude — o gesto mais destrutivo do painel agindo na conversa errada.
    mutationFn: (motor: string) =>
      chamar<unknown>(`${ROTAS.sessoes}/mestre/recomecar`, {
        method: 'POST',
        corpo: { confirmacaoMestre: true, motor },
      }),
    onMutate: () => setErroDaAcao(''),
    onError: (e) => setErroDaAcao(mensagemDoErro(e, 'Não foi possível recomeçar o mestre.')),
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['sessoes'] }),
  });

  function comecarRenomear(sessao: SessaoNaLista): void {
    setRenomeando(sessao.id);
    setRascunho(sessao.nome ?? sessao.tituloIa ?? sessao.nomeMaquina ?? '');
    // O campo nasce montado no mesmo quadro; focar no seguinte.
    setTimeout(() => campo.current?.select(), 0);
  }

  function salvarRenomear(id: string): void {
    const nome = rascunho.trim();
    setRenomeando('');
    if (nome.length > 0) renomear.mutate({ id, nome });
  }

  /*
    As mestres vigentes saem dos grupos: elas moram fixadas no topo.

    PLURAL desde o segundo motor. Com `acharMestre` (singular) a segunda
    SUMIA da tela: `ehMestreVigente` a tirava dos grupos e o topo so cabia
    uma. A sessao existia, respondia e ocupava vaga — e o painel nao a
    mostrava em lugar nenhum.
  */
  const mestres = useMemo(() => acharMestres(sessoes), [sessoes]);

  /*
    O que sobra depois da busca — antes do filtro de projeto.

    A ordem importa: as opções da pílula de projeto saem DESTA lista, e não da
    filtrada. Tiradas da filtrada, escolher "Remoto" deixaria a pílula com uma
    opção só — "Remoto" — e não haveria como trocar para outro projeto sem
    limpar tudo antes. Filtro que se apaga do menu é filtro sem volta.
  */
  /*
    O que mora DENTRO de cada mestre (vigiadas e mestres anteriores) sai da
    lista antes de qualquer filtro: o resto é o que segue para os grupos.
  */
  const { porMestre, resto } = useMemo(() => separarDaMestre(sessoes, mestres), [sessoes, mestres]);

  const busca = filtro.trim().toLowerCase();
  const visiveis = useMemo(() => {
    return resto.filter((s) => {
      if (ehMestreVigente(s)) return false;
      if (!mostrarEncerradas && s.estado === 'ENCERRADA') return false;
      return !busca || bateComABusca(s, busca);
    });
  }, [resto, busca, mostrarEncerradas]);

  /*
    A busca também olha DENTRO das mestres: com texto digitado, só as filhas que
    batem, e a subárvore abre enquanto a busca durar (sem gravar nada: apagado o
    texto, volta ao que o aparelho tinha guardado).
  */
  const filhasVisiveis = useMemo(
    () => filtrarFilhasDaMestre(porMestre, busca, (s) => bateComABusca(s, busca)),
    [porMestre, busca],
  );

  /*
    O projeto escolhido entra na lista de opções mesmo que não esteja mais na
    lista de sessões.

    Ele pode ter sido arquivado, encerrado ou ter saído das 200 linhas desde a
    última visita. Sem isto, a pílula abriria em "Todos os projetos" enquanto o
    filtro continua valendo — a lista vazia e o controle jurando que não há
    filtro nenhum.
  */
  const projetos = useMemo(() => {
    const presentes = projetosDe(visiveis);
    const escolhido = chaveDoProjeto(arranjo.projeto);
    if (!arranjo.projeto || presentes.some((p) => p.chave === escolhido)) return presentes;
    return [...presentes, { chave: escolhido, rotulo: nomeDoProjeto(arranjo.projeto) }];
  }, [visiveis, arranjo.projeto]);

  const grupos = useMemo(() => {
    // Chave contra chave, e não caminho contra caminho: é o que faz `C:\...\Remoto`
    // e `c:/.../remoto/` serem o mesmo projeto — e o que faz uma escolha
    // guardada como caminho inteiro, por uma versão antiga, continuar valendo.
    const escolhido = chaveDoProjeto(arranjo.projeto);
    const doProjeto = arranjo.projeto
      ? visiveis.filter((s) => chaveDoProjeto(s.cwd) === escolhido)
      : visiveis;
    return agruparSessoes(
      ordenarSessoes(doProjeto, arranjo.ordenar, nomeDaSessao),
      arranjo.agrupar,
      Date.now(),
    );
  }, [visiveis, arranjo.projeto, arranjo.ordenar, arranjo.agrupar]);

  /*
    Encostada no teto, a lista diz que está.

    O servidor manda uma página só, sem cursor, e o corte pega as MAIS ANTIGAS.
    Calada, "Mais antigas" mostraria as mais antigas DESTAS 200 e pareceria a
    resposta certa para "qual é a conversa mais parada que eu tenho?" — que é
    exatamente a pergunta que ela erraria.
  */
  const noTeto = sessoes.length >= TETO_LISTA_SESSOES;

  /*
    O % de uso só das linhas que estão na tela: as mestres e o que sobrou do
    filtro. Pedir as duzentas a cada minuto seria pagar por selo que ninguém
    vê — e a chave ordenada faz o giro de dez segundos da lista não disparar
    consulta nova quando nada mudou.
  */
  const usoDasSessoes = usarUsoDasSessoes([
    ...mestres.map((m) => m.id),
    ...mestres.flatMap((m) =>
      busca || expandidas.includes(m.id)
        ? [
            ...(filhasVisiveis.get(m.id)?.vigiadas ?? []),
            ...(filhasVisiveis.get(m.id)?.anteriores ?? []),
          ].map((s) => s.id)
        : [],
    ),
    ...grupos.flatMap((grupo) => grupo.sessoes.map((s) => s.id)),
  ]);

  // As anteriores moram dentro da mestre e não entram na conta.
  const encerradas = resto.filter((s) => s.estado === 'ENCERRADA').length;
  const arquivadas = sessoes.filter((s) => s.arquivadaEm).length;

  function alternarMestre(id: string): void {
    // Durante a busca a subárvore está aberta à força, e o que se grava é do aparelho.
    if (busca) return;
    setExpandidas((atual) => {
      const novo = atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id];
      gravarMestresExpandidas(novo);
      return novo;
    });
  }

  /*
    Abre sozinha a mestre que guarda a conversa aberta: quem chegou por um link
    não pode ter a conversa dele escondida na lista. Uma vez por conversa
    aberta — senão o recolher manual seria desfeito a cada giro da lista.
  */
  const abertaSozinhaPara = useRef('');
  useEffect(() => {
    if (!sessaoAtiva || abertaSozinhaPara.current === sessaoAtiva) return;
    for (const [idMestre, filhas] of porMestre) {
      if ([...filhas.vigiadas, ...filhas.anteriores].some((s) => s.id === sessaoAtiva)) {
        abertaSozinhaPara.current = sessaoAtiva;
        setExpandidas((atual) => {
          if (atual.includes(idMestre)) return atual;
          const novo = [...atual, idMestre];
          gravarMestresExpandidas(novo);
          return novo;
        });
        return;
      }
    }
  }, [sessaoAtiva, porMestre]);

  const acoes: AcoesDaLinha = {
    sessaoAtiva,
    renomeando,
    rascunho,
    campo,
    menuAberto,
    setRascunho,
    setRenomeando,
    setMenuAberto,
    salvarRenomear,
    comecarRenomear,
    aoEscolher,
    silenciar: (id, silenciada) => silenciar.mutate({ id, silenciada }),
    arquivar: (id, arquivada) => arquivar.mutate({ id, arquivada }),
    adormecer: (id) => adormecer.mutate(id),
    encerrar: (id) => encerrar.mutate(id),
    confirmar,
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        Uma fileira só, e não duas.

        Antes a barra de arranjo morava numa linha própria e, numa coluna de
        300 px, as pílulas com texto quebravam em mais uma: três fileiras de
        acessório antes de a lista começar, e num tablet em paisagem a lista
        terminava espremida no rodapé. Como ícones, os três controles cabem ao
        lado da busca — e o acessório volta a custar o que um acessório custa.
      */}
      <div data-testid="cabecalho-da-lista" className="shrink-0 border-b border-zinc-800 px-2 py-2">
        {/*
          Quem cede espaço são os ÍCONES, não o campo.

          Era o contrário, e dava no que o dono viu: arrastando a divisória, a
          coluna em 182 px deixava o campo com 18 — uma tira sem nem o
          "Filtrar…" caber — enquanto os três ícones seguiam inteiros. Mas o
          campo é onde ele digita: ícone apertado ainda se clica, campo de 18 px
          não serve para nada.

          Por isso o campo ganha piso (`min-w-24`, 96 px) e o grupo de botões vira
          `shrink-0`: quando os dois não cabem juntos, é o grupo que desce para
          a segunda fileira, e o campo fica sozinho em cima com a largura toda.
          Na coluna larga nada disso acontece e a fileira única continua.

          O piso é 96 px, e não mais: na largura padrão do desktop (273 px) o
          campo fica com 109, e a fileira única sobrevive. Cabe porque foi
          medido — "Filtrar…" ocupa 39 px na fonte real do campo, e com os
          16 de padding e 2 de borda precisa de 57. Um piso maior empurrava o
          padrão do desktop para duas fileiras, que é mudança que ninguém
          pediu na tela mais usada: a quebra é para quando ELE diminui.
        */}
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Filtrar…"
            aria-label="Filtrar sessões"
            className="min-w-24 flex-1 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1 text-xs text-zinc-200 placeholder:text-zinc-600 pointer-coarse:min-h-11 pointer-coarse:px-3"
          />
          {/*
            O "+" e a barra são IRMÃOS do campo — nada de um grupo em volta.

            Eles moravam dentro de um `<div>` só, para descer juntos para a
            segunda fileira em vez de se espalharem conforme o pixel. O preço
            dessa arrumação só apareceu no telefone: um grupo aninhado é
            ATÔMICO para a fileira de fora, então quando a barra não cabia ao
            lado do "+", quem descia era a barra INTEIRA — e o "+" ficava
            sozinho ocupando uma fileira de 44 px para mostrar um ícone.

            Medido em 390 px com filtro de projeto e ordenação escolhidos:
            quatro fileiras (campo / "+" / pílulas / pílulas) e **207 px** de
            cabeçalho, um quarto da tela do aparelho antes de a lista começar.
            Sendo irmãos, o "+" volta para a fileira do campo e sobram três:
            160 px. E o encolhimento do rótulo das pílulas não tinha nada a ver
            — de 22ch a 10ch o cabeçalho media 207 nos cinco casos.

            O que o grupo protegia continua protegido, e por quem devia: o
            último ícone não termina fora da coluna porque a PRÓPRIA barra
            quebra por dentro (`min-w-0` em `BarraDeSessoes`), e o campo não
            vira uma tira porque o piso é dele (`min-w-24`).
          */}
          <Link
            to="/sessoes/nova"
            title="Nova sessão"
            aria-label="Nova sessão"
            className="flex shrink-0 items-center justify-center rounded-md border border-zinc-700 px-2 py-1 text-zinc-300 hover:bg-zinc-800 pointer-coarse:h-11 pointer-coarse:w-11"
          >
            <IconeMais tamanho={14} />
          </Link>
          <BarraDeSessoes arranjo={arranjo} aoMudar={mudarArranjo} projetos={projetos} />
        </div>
      </div>

      <div data-testid="rolagem-da-lista" className="min-h-0 flex-1 overflow-y-auto px-1 py-1">
        {isLoading ? <p className="px-2 py-1 text-xs text-zinc-500">carregando…</p> : null}
        {/*
          Lista vazia POR CAUSA do filtro é uma mensagem diferente de lista
          vazia — e vem com a saída junto. Sem isso, um filtro de projeto que
          sobreviveu ao recarregamento parece o painel ter perdido as conversas.
          Aparece mesmo com a mestre no topo: é o corpo da lista que está vazio.
        */}
        {!isLoading && grupos.length === 0 && arranjo.projeto ? (
          <p className="px-2 py-1 text-xs text-zinc-500">
            Nenhuma sessão neste projeto.{' '}
            <button
              onClick={() => mudarArranjo({ projeto: '' })}
              className="underline hover:text-zinc-300"
            >
              ver todos
            </button>
          </p>
        ) : !isLoading && grupos.length === 0 && mestres.length === 0 ? (
          <p className="px-2 py-1 text-xs text-zinc-500">Nenhuma sessão para mostrar.</p>
        ) : null}

        {/* As sessões mestre (F13), fixadas acima de todas as máquinas — uma
            por motor, na ordem dos motores. */}
        {mestres.map((mestre) => {
          const filhas = filhasVisiveis.get(mestre.id) ?? { vigiadas: [], anteriores: [] };
          const aberta = busca
            ? filhas.vigiadas.length + filhas.anteriores.length > 0
            : expandidas.includes(mestre.id);
          const resumo = resumoDaMestre(filhas);
          const vigiadaEsperando = filhas.vigiadas.some((s) => s.estado === 'AGUARDANDO_PERMISSAO');
          const vigiadaTrabalhando =
            vigiadaEsperando || filhas.vigiadas.some((s) => s.estado === 'ATIVA');
          return (
            <section key={mestre.id} className="mb-2" data-testid="sessao-mestre">
              <ul className="space-y-px">
                <li>
                  <button
                    onClick={() => aoEscolher(mestre)}
                    title={mestre.cwd}
                    className={
                      'flex w-full items-start gap-2 rounded py-1 pl-2 pr-2 text-left transition ' +
                      'pointer-coarse:min-h-12 pointer-coarse:py-2 pointer-coarse:pl-3 ' +
                      (mestre.id === sessaoAtiva
                        ? 'bg-zinc-800 text-zinc-100'
                        : 'text-zinc-300 hover:bg-zinc-800/60')
                    }
                  >
                    <span
                      title={ROTULO[mestre.estado]}
                      className={'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ' + COR[mestre.estado]}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={
                          'flex items-center gap-1.5 truncate text-sm ' +
                          (temNovidade(mestre) && mestre.id !== sessaoAtiva
                            ? 'font-semibold text-zinc-50'
                            : '')
                        }
                      >
                        {nomeDaSessao(mestre)}
                        <span className="inline-flex items-center gap-1 rounded bg-sky-900/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-300">
                          <IconeMestre tamanho={10} />
                          {/* Com duas, o selo diz QUAL: dois cartões "mestre"
                            iguais não dizem com quem se está falando. */}
                          mestre {ROTULO_DO_MOTOR[(mestre.motor ?? 'CLAUDE') as Motor]}
                        </span>
                      </span>
                      <span className="block truncate text-[11px] text-zinc-500">
                        <SeloDeUso uso={usoDasSessoes[mestre.id]} />
                        {mestre.maquinaNome} ·{' '}
                        {tempoRelativo(mestre.ultimoEventoEm, 'sem atividade')}
                      </span>
                    </span>
                    {temNovidade(mestre) && mestre.id !== sessaoAtiva ? (
                      <span
                        title="concluiu desde a última vez que você viu"
                        className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-400"
                      />
                    ) : null}
                  </button>
                  {/*
                  Recomeçar é a ação que o mestre pede e nenhuma outra sessão
                  pede: a conversa dele cresce até o contexto ser compactado, e
                  a qualidade cai — mas a memória dele NÃO está na conversa,
                  está no disco, na pasta de trabalho (`MEMORIA.md`, `tarefas/`),
                  que ele relê ao começar. Trocar a conversa mantendo a pasta é
                  chat limpo sem amnésia.

                  Por isso não há "Arquivar" aqui: ele ocupa a vaga única da
                  frota enquanto não for encerrado, e sumir com ele da lista
                  esconderia o motivo de o botão de criar outro não aparecer.
                */}
                  {papel === 'ADMIN' ? (
                    <button
                      onClick={() =>
                        confirmar({
                          titulo: 'Recomeçar a sessão mestre?',
                          texto:
                            'A conversa atual é encerrada e outra começa na mesma pasta — que é ' +
                            'onde a memória dele mora, então nada do que ele anotou se perde. ' +
                            'O histórico desta conversa fica guardado.',
                          rotuloConfirmar: 'Recomeçar',
                          aoConfirmar: () => recomecarMestre.mutate(mestre.motor ?? 'CLAUDE'),
                        })
                      }
                      disabled={recomecarMestre.isPending}
                      className="mx-2 mt-1 flex items-center gap-1.5 rounded px-1 py-0.5 text-[11px] text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-50 pointer-coarse:min-h-9"
                    >
                      <IconeRecomecar tamanho={11} />
                      {recomecarMestre.isPending ? 'recomeçando…' : 'recomeçar com a memória'}
                    </button>
                  ) : null}
                  {/*
                  Vigiadas e mestres anteriores moram DENTRO da mestre,
                  recolhidas. O ponto acende quando algo vigiado trabalha ou
                  espera você — recolhido, é o único sinal de que vale abrir.
                */}
                  {filhas.vigiadas.length + filhas.anteriores.length > 0 ? (
                    <div className="mx-2 mt-1 flex min-w-0 items-center gap-1.5">
                      <button
                        onClick={() => alternarMestre(mestre.id)}
                        aria-expanded={aberta}
                        data-testid="mestre-expandir"
                        aria-label={(aberta ? 'Recolher ' : 'Expandir ') + resumo}
                        className="flex min-w-0 items-center gap-1 rounded px-1 py-0.5 text-[11px] text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200 pointer-coarse:min-h-9"
                      >
                        {aberta ? <IconeAbrir tamanho={12} /> : <IconeFechado tamanho={12} />}
                        <span className="truncate">{resumo}</span>
                      </button>
                      {!aberta && vigiadaTrabalhando ? (
                        <span
                          data-testid="mestre-vigiada-ativa"
                          title={
                            vigiadaEsperando
                              ? 'uma conversa vigiada espera permissão'
                              : 'uma conversa vigiada está ativa'
                          }
                          className={
                            'h-1.5 w-1.5 shrink-0 rounded-full ' +
                            (vigiadaEsperando ? COR.AGUARDANDO_PERMISSAO : COR.ATIVA)
                          }
                        />
                      ) : null}
                    </div>
                  ) : null}
                  {aberta ? (
                    <div
                      data-testid="mestre-subarvore"
                      className="ml-3 mt-1 min-w-0 border-l border-zinc-800 pl-2"
                    >
                      {filhas.vigiadas.length > 0 ? (
                        <>
                          <p className="px-2 py-0.5 text-[10px] uppercase tracking-wide text-zinc-600">
                            Vigiadas
                          </p>
                          <ul className="space-y-px">
                            {filhas.vigiadas.map((s) => (
                              <LinhaDaSessao
                                key={s.id}
                                sessao={s}
                                uso={usoDasSessoes[s.id]}
                                acoes={acoes}
                                vigiada
                              />
                            ))}
                          </ul>
                        </>
                      ) : null}
                      {filhas.anteriores.length > 0 ? (
                        <>
                          <p className="px-2 py-0.5 text-[10px] uppercase tracking-wide text-zinc-600">
                            Mestres anteriores
                          </p>
                          <ul className="space-y-px">
                            {filhas.anteriores.map((s) => (
                              <LinhaDaSessao
                                key={s.id}
                                sessao={s}
                                uso={usoDasSessoes[s.id]}
                                acoes={acoes}
                              />
                            ))}
                          </ul>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              </ul>
            </section>
          );
        })}

        {/* Falta alguma? O botão continua ali — quem decide se cabe é a tela
            de criação, que lê a capacidade de cada máquina. */}
        {mestres.length < MOTORES.length && papel === 'ADMIN' ? (
          <button
            onClick={() => setCriandoMestre(true)}
            className="mx-2 mb-2 flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-300"
          >
            <IconeMestre tamanho={12} />
            criar sessão mestre
          </button>
        ) : null}

        {grupos.map((grupo) => (
          <section key={grupo.chave} className="mb-2">
            {/* "Sem grupos" não tem cabeçalho — uma faixa em branco no lugar
                dele custaria uma linha de lista e não diria nada. */}
            {grupo.rotulo ? (
              <p
                title={grupo.rotulo}
                className="truncate px-2 py-1 text-[11px] uppercase tracking-wide text-zinc-600"
              >
                {grupo.rotulo}
              </p>
            ) : null}
            <ul className="space-y-px">
              {grupo.sessoes.map((sessao) => (
                <LinhaDaSessao
                  key={sessao.id}
                  sessao={sessao}
                  uso={usoDasSessoes[sessao.id]}
                  acoes={acoes}
                />
              ))}
            </ul>
          </section>
        ))}

        {noTeto ? (
          <p className="mx-2 my-1 text-[11px] leading-snug text-zinc-600">
            Mostrando as {TETO_LISTA_SESSOES} conversas mais recentes. As mais antigas ficaram de
            fora, e ordenar aqui não as traz de volta.
          </p>
        ) : null}

        {encerradas > 0 ? (
          <button
            onClick={() => setMostrarEncerradas((v) => !v)}
            className="mx-2 my-1 block text-xs text-zinc-600 hover:text-zinc-400"
          >
            {mostrarEncerradas ? 'esconder' : 'mostrar'} {encerradas} encerrada(s)
          </button>
        ) : null}

        {/*
          Este botão aparece SEMPRE, e não só quando há arquivadas na lista:
          quando elas estão escondidas, a lista não sabe quantas são — é
          justamente a informação que o servidor não mandou. Sem ele, arquivar
          seria um caminho sem volta pela tela.
        */}
        <button
          onClick={() => setMostrarArquivadas((v) => !v)}
          className="mx-2 my-1 block text-xs text-zinc-600 hover:text-zinc-400"
        >
          {mostrarArquivadas ? `esconder ${arquivadas} arquivada(s)` : 'mostrar arquivadas'}
        </button>
      </div>

      {erroDaAcao ? (
        <p
          role="alert"
          className="mx-2 mb-2 rounded border border-red-900/60 bg-red-950/30 px-2 py-1.5 text-xs text-red-200"
        >
          {erroDaAcao}
        </p>
      ) : null}

      {dialogo}
      <CriarMestre aberto={criandoMestre} aoFechar={() => setCriandoMestre(false)} />
    </div>
  );
}

/** "3 vigiadas · 2 anteriores": a parte zerada fica de fora. */
function resumoDaMestre(filhas: FilhasDaMestre<SessaoNaLista>): string {
  const partes: string[] = [];
  const v = filhas.vigiadas.length;
  const a = filhas.anteriores.length;
  if (v > 0) partes.push(v === 1 ? '1 vigiada' : `${v} vigiadas`);
  if (a > 0) partes.push(a === 1 ? '1 anterior' : `${a} anteriores`);
  return partes.join(' · ');
}

/** Tudo o que uma linha da lista precisa do pai: estado de edição e ações. */
interface AcoesDaLinha {
  sessaoAtiva: string;
  renomeando: string;
  rascunho: string;
  campo: RefObject<HTMLInputElement | null>;
  menuAberto: string;
  setRascunho: (texto: string) => void;
  setRenomeando: (id: string) => void;
  setMenuAberto: (atualiza: (atual: string) => string) => void;
  salvarRenomear: (id: string) => void;
  comecarRenomear: (sessao: SessaoNaLista) => void;
  aoEscolher: (sessao: SessaoNaLista) => void;
  silenciar: (id: string, silenciada: boolean) => void;
  arquivar: (id: string, arquivada: boolean) => void;
  adormecer: (id: string) => void;
  encerrar: (id: string) => void;
  confirmar: ReturnType<typeof usarConfirmacao>[0];
}

/** Se a conversa bate com o texto digitado em "Filtrar..." (`busca` já em minúsculas). */
function bateComABusca(s: SessaoNaLista, busca: string): boolean {
  return [s.tituloIa, s.nome, s.nomeMaquina, s.cwd, s.maquinaNome]
    .filter(Boolean)
    .some((campo) => (campo as string).toLowerCase().includes(busca));
}

/**
 * Uma conversa na lista — a mesma linha nos grupos e dentro da mestre.
 *
 * `vigiada` põe a marca de quem a mestre acompanha: o olho e o fundo que
 * respira (`.vigiada-respira`, em `estilos.css`). A linha ABERTA não respira —
 * o destaque dela é o fundo, e a animação o apagaria.
 */
function LinhaDaSessao({
  sessao,
  uso,
  acoes,
  vigiada = false,
}: {
  sessao: SessaoNaLista;
  uso: UsoDaSessao | undefined;
  acoes: AcoesDaLinha;
  vigiada?: boolean;
}): React.JSX.Element {
  const sinal = sessao.id === acoes.sessaoAtiva ? 'nenhum' : sinalDeNovidade(sessao);
  // Só o sinal aceso engrossa o nome. O discreto não disputa a leitura da
  // lista — é para ser encontrado, não para saltar.
  const novidade = sinal === 'aceso';
  if (acoes.renomeando === sessao.id) {
    return (
      <li className="flex items-center gap-1 px-2 py-1">
        <input
          ref={acoes.campo}
          value={acoes.rascunho}
          onChange={(e) => acoes.setRascunho(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') acoes.salvarRenomear(sessao.id);
            if (e.key === 'Escape') acoes.setRenomeando('');
          }}
          maxLength={80}
          aria-label="Nome da sessão"
          className="min-w-0 flex-1 rounded border border-zinc-600 bg-zinc-950 px-1.5 py-0.5 text-sm text-zinc-100"
        />
        <button
          onClick={() => acoes.salvarRenomear(sessao.id)}
          aria-label="Salvar nome"
          title="Salvar"
          className="text-zinc-400 hover:text-zinc-100"
        >
          <IconeConfirmado tamanho={14} />
        </button>
        <button
          onClick={() => acoes.setRenomeando('')}
          aria-label="Cancelar"
          title="Cancelar"
          className="text-zinc-500 hover:text-zinc-200"
        >
          <IconeFechar tamanho={14} />
        </button>
      </li>
    );
  }
  const aberta = sessao.id === acoes.sessaoAtiva;
  return (
    <li className="group/linha relative" data-vigiada={vigiada ? 'true' : undefined}>
      <button
        onClick={() => acoes.aoEscolher(sessao)}
        title={sessao.cwd}
        className={
          'flex w-full items-start gap-2 rounded py-1 pl-2 pr-7 text-left transition ' +
          // A linha tem duas alturas de texto e `py-1`: no dedo fica em 40 px
          // raspando, e é o alvo mais tocado da tela inteira.
          'pointer-coarse:min-h-12 pointer-coarse:py-2 pointer-coarse:pl-3 ' +
          (aberta
            ? 'bg-zinc-800 text-zinc-100'
            : 'text-zinc-300 hover:bg-zinc-800/60' +
              (vigiada && sessao.estado !== 'ENCERRADA' ? ' vigiada-respira' : ''))
        }
      >
        <span
          title={ROTULO[sessao.estado]}
          className={'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ' + COR[sessao.estado]}
        />
        <span className="min-w-0 flex-1">
          <span
            className={'block truncate text-sm ' + (novidade ? 'font-semibold text-zinc-50' : '')}
          >
            {vigiada ? (
              <span
                role="img"
                title="vigiada pela mestre"
                aria-label="vigiada pela mestre"
                className="mr-1 inline-block align-[-1px] text-zinc-500"
              >
                <IconeMostrar tamanho={11} />
              </span>
            ) : null}
            {nomeDaSessao(sessao)}
          </span>
          <span className="block truncate text-[11px] text-zinc-500">
            <SeloDeModo modo={sessao.modo} />
            {sessao.modo === 'auto' || sessao.modo === 'bypassPermissions' ? ' ' : ''}
            {/*
              O selo do motor aparece SÓ no que foge do padrão da casa. Marcar
              as duas encheria a lista inteira de etiqueta e faria a informação
              virar textura: numa frota de trinta sessões do Claude, o que se
              precisa ver é a que não é.
            */}
            <SeloDoMotorNaLista motor={sessao.motor} />
            <SeloDeUso uso={uso} />
            {pastaCurta(sessao.cwd)} · {tempoRelativo(sessao.ultimoEventoEm, 'sem atividade')}
            {sessao.origem === 'SDK' ? '' : ' · VS Code'}
          </span>
        </span>
        {/*
          O sino cortado fica na linha, e não só no menu: quem silencia e
          esquece que silenciou vive um aviso que sumiu sem explicação — pior
          do que aviso demais.
        */}
        {sessao.silenciada ? <IconeSilenciada tamanho={12} className="mt-1 text-zinc-600" /> : null}
        {sinal !== 'nenhum' ? (
          <span
            title={
              sinal === 'discreto'
                ? 'concluiu desde a última vez que você viu — avisos silenciados'
                : 'concluiu desde a última vez que você viu'
            }
            aria-label={
              sinal === 'discreto'
                ? 'concluiu desde a última vez que você viu — avisos silenciados'
                : 'concluiu desde a última vez que você viu'
            }
            className={
              'mt-1.5 h-2 w-2 shrink-0 rounded-full ' +
              (sinal === 'discreto' ? 'bg-zinc-600' : 'bg-sky-400')
            }
          />
        ) : null}
      </button>
      {/*
        O ⋯ só aparece no hover e no foco: uma coluna de ícones sempre visível
        competiria com o nome, que é o que a pessoa lê para escolher. No dedo
        não há hover, e por isso ele fica sempre visível ali.
      */}
      <button
        onClick={() => acoes.setMenuAberto((atual) => (atual === sessao.id ? '' : sessao.id))}
        aria-label={'Ações de ' + nomeDaSessao(sessao)}
        aria-expanded={acoes.menuAberto === sessao.id}
        title="Ações"
        className={
          'absolute right-1 top-1 rounded p-0.5 text-zinc-600 transition hover:bg-zinc-700 hover:text-zinc-200 focus:opacity-100 group-hover/linha:opacity-100 ' +
          'pointer-coarse:opacity-100 pointer-coarse:p-2 ' +
          (acoes.menuAberto === sessao.id ? 'opacity-100 text-zinc-200' : 'opacity-0')
        }
      >
        <IconeMaisOpcoes tamanho={13} />
      </button>
      {acoes.menuAberto === sessao.id ? (
        <MenuDaSessao
          sessao={sessao}
          aoFechar={() => acoes.setMenuAberto(() => '')}
          aoRenomear={() => acoes.comecarRenomear(sessao)}
          aoSilenciar={(silenciada) => acoes.silenciar(sessao.id, silenciada)}
          aoArquivar={(arquivada) => acoes.arquivar(sessao.id, arquivada)}
          aoAdormecer={() => acoes.adormecer(sessao.id)}
          aoEncerrar={() =>
            acoes.confirmar({
              titulo: 'Encerrar esta conversa?',
              /*
                É aqui que a escolha errada acontece, então é aqui que as três
                saídas precisam estar escritas. Quem abre este diálogo quase
                sempre quer vaga na máquina — e para isso Adormecer é melhor,
                porque devolve a vaga sem perder a conversa. O texto antigo só
                citava Arquivar, que é justamente o gesto que NÃO devolve vaga.
              */
              texto:
                'A sessão para de verdade na máquina, e não tem volta. Se você ' +
                'quer liberar vaga, use Adormecer: devolve a vaga e guarda a ' +
                'conversa inteira. Para só tirá-la da lista, use Arquivar.',
              rotuloConfirmar: 'Encerrar',
              perigosa: true,
              aoConfirmar: () => acoes.encerrar(sessao.id),
            })
          }
        />
      ) : null}
    </li>
  );
}

/**
 * As ações de uma linha da lista, atrás do ⋯.
 *
 * Três ações não cabem como três ícones: a linha tem 40 px de altura e o nome
 * da conversa é o que se lê para escolher — uma coluna de ícones competindo
 * com ele deixaria a lista ilegível justamente na largura em que ela vive.
 *
 * Encerrar pede confirmação porque não tem volta; arquivar não pede, porque
 * desarquivar está a um clique de distância no rodapé da lista.
 */
function MenuDaSessao({
  sessao,
  aoFechar,
  aoRenomear,
  aoSilenciar,
  aoArquivar,
  aoAdormecer,
  aoEncerrar,
}: {
  sessao: SessaoNaLista;
  aoFechar: () => void;
  aoRenomear: () => void;
  aoSilenciar: (silenciada: boolean) => void;
  aoArquivar: (arquivada: boolean) => void;
  aoAdormecer: () => void;
  aoEncerrar: () => void;
}): React.JSX.Element {
  const arquivada = Boolean(sessao.arquivadaEm);
  const silenciada = sessao.silenciada === true;
  const encerrada = sessao.estado === 'ENCERRADA';
  /*
    Adormecer só faz sentido em sessão do painel que ainda tem processo vivo.
    Quem já dorme não tem o que devolver, e a conversa do editor nunca teve
    processo hospedado pelo daemon.
  */
  const dormindo = sessao.estado === 'DORMINDO';
  /*
    Conversa do editor não se encerra por aqui.

    Ela nasceu no VS Code e o painel só a observa: o daemon nunca a hospedou,
    então o pedido voltava "não conheço essa sessão" — traduzido na tela como
    **"máquina desligada"**, sobre uma máquina que respondeu na hora. O painel
    da conversa já escondia o botão nesse caso; este menu foi ao ar sem a mesma
    guarda. Para tirar da vista existe Arquivar, que é o que ela precisa.
  */
  const daSdk = sessao.origem === 'SDK';

  /*
    Fechar ao tocar fora, e no Esc.

    `pointerdown` e nao `click`: no iOS um toque em elemento que nao e
    clicavel nao sintetiza evento de mouse, e o menu ficaria aberto para
    sempre justamente no aparelho em que ele mais atrapalha.
  */
  useEffect(() => {
    const aoTocarFora = (): void => aoFechar();
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') aoFechar();
    };
    // No quadro seguinte: o mesmo toque que abriu o menu o fecharia.
    const id = setTimeout(() => document.addEventListener('pointerdown', aoTocarFora), 0);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', aoTocarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aoFechar]);

  const item =
    'block w-full rounded px-2 py-1.5 text-left text-xs text-zinc-300 transition hover:bg-zinc-800 pointer-coarse:py-2.5';

  return (
    <div
      role="menu"
      aria-label={'Ações de ' + nomeDaSessao(sessao)}
      onPointerDown={(e) => e.stopPropagation()}
      className="absolute right-1 top-7 z-30 w-44 rounded-md border border-zinc-700 bg-zinc-900 p-1 shadow-lg shadow-black/40"
    >
      <button
        className={item}
        onClick={() => {
          aoFechar();
          aoRenomear();
        }}
      >
        Renomear
      </button>
      {/*
        Silenciar fica acima de Arquivar porque é o gesto mais leve dos dois, e
        os dois respondem à mesma vontade ("me deixa em paz com esta conversa").
        A diferença é o preço: silenciar não muda nada para mais ninguém;
        arquivar tira a linha da lista de todo mundo.
      */}
      <button
        className={item}
        onClick={() => {
          aoFechar();
          aoSilenciar(!silenciada);
        }}
      >
        {silenciada ? 'Reativar avisos' : 'Silenciar avisos'}
      </button>
      <button
        className={item}
        onClick={() => {
          aoFechar();
          aoArquivar(!arquivada);
        }}
      >
        {arquivada ? 'Tirar do arquivo' : 'Arquivar'}
      </button>
      {/*
        Adormecer fica ENTRE arquivar e encerrar, e é essa a ordem da escada:
        silenciar não muda nada para ninguém, arquivar tira da vista, adormecer
        devolve a vaga guardando a conversa, encerrar mata. Posto depois do
        encerrar, seria lido como variação dele; posto antes do arquivar,
        pareceria mais leve do que é. A legenda existe porque a diferença entre
        os três é invisível no rótulo — e escolher errado aqui custou, num dia
        de falta de vaga, cinco conversas encerradas à toa.
      */}
      {!encerrada && !dormindo && daSdk ? (
        <button
          className={item}
          onClick={() => {
            aoFechar();
            aoAdormecer();
          }}
        >
          Adormecer
          <span className="mt-0.5 block text-[11px] leading-snug text-zinc-500">
            devolve a vaga e guarda a conversa
          </span>
        </button>
      ) : null}
      {!encerrada && daSdk ? (
        <button
          className={item + ' text-red-300 hover:bg-red-950/40'}
          onClick={() => {
            aoFechar();
            aoEncerrar();
          }}
        >
          Encerrar
        </button>
      ) : null}
      {!encerrada && !daSdk ? (
        <p className="px-2 py-1.5 text-[11px] leading-snug text-zinc-600">
          Quem encerra esta conversa é o VS Code.
        </p>
      ) : null}
    </div>
  );
}
